import { Navigate, Outlet, RouteObject } from 'react-router-dom';
import {
  ALL_ACCESS,
  canAccessEditor,
  canAccessPeople,
  canAccessRtmp,
  DEVELOPER_ONLY,
  EPermissions,
} from '@/types/permissions.types';
import { ICONS } from '@ui/icons/types';
import EditProjectErrorBoundary from '../grid-areas/main/edit-project-error-boundary/edit-project-error-boundary';
import MainContentWrapper from '../../shared-create-and-edit-components/grid-areas/main/project-overview/main-content-wrapper';
import RouteGuard from '@ui/route-guard/route-guard';
import ProjectOverview from '../grid-areas/main/project-overview/project-overview';
import ProjectSession from '../grid-areas/main/project-overview/project-session';
import { getBaseAdminURL } from '@/utils/urls';
import Settings from '../grid-areas/main/settings/settings';
import ComingSoon from '@/micros/coming-soon/coming-soon';
// import StatsCleanupWrapper from '../grid-areas/main/analytics/stats-cleanup-wrapper.tsx';
import CastAndCrew from '../grid-areas/main/cast-and-crew/cast-and-crew.tsx';
import Stylepacks from '../grid-areas/main/stylepacks/stylepacks.tsx';
import { stylepacksLoader } from '../grid-areas/main/stylepacks/stylepacks.loader.ts';
import BrowseStylepacks from '../grid-areas/main/stylepacks/browse/browse-stylepacks.tsx';
import PreviewStylepack from '../grid-areas/main/stylepacks/preview/preview-stylepack.tsx';
import Audiences from '../grid-areas/main/audiences/audiences.tsx';
import Registrants from '../grid-areas/main/audiences/components/registrants/audiences-registrants.tsx';
import { AUDIENCES_VIEW } from '@/types/registration.types.ts';
import Campaigns from '../grid-areas/main/campaigns/campaigns.tsx';
import CampaignEditor from '../grid-areas/main/campaigns/editor/campaign-editor.tsx';
import AllProfiles from '../grid-areas/main/audiences/components/listsv2/all-profiles.tsx';
import AudiencesRouteGate from '../grid-areas/main/audiences/audiences-route-gate.tsx';
import { audiencesLoader } from '../grid-areas/main/audiences/loaders/audiences-main.loader.ts';
import AudienceList from '../grid-areas/main/audiences/components/listsv2/audience-list.tsx';
import { READ_ONLY_PROJECT_ROLES } from './read-only-project-access';
import { EDIT_PROJECT_ROUTE_IDS } from './edit-project-route-ids';
import SettingsRoutes from '../grid-areas/main/settings/settings.routes.tsx';

export type EditProjectRouteConfig = RouteObject & {
  id: string;
  link_text: string;
  allowedRoles?: EPermissions[];
  blockedRoles?: EPermissions[];
  redirectTo?: string;
  onClick?: () => void; // For links that are functions that check some stuff and then redirect
  unauthorizedMessage?: string;
  icon?: string;
  isIndex?: boolean;
  notAvailable?: boolean;
};

const GENERAL_ACCESS_ROLES = [
  EPermissions.GroupAdmin,
  EPermissions.Editor,
  EPermissions.Moderation,
  EPermissions.Producer,
];

// Define simplified permission configurations
// Note: owner/admin/builder roles by default are allowed access (see use-has-permission.ts)
const ROUTE_PERMISSIONS = {
  // Admin-only routes
  adminOnly: {
    allowedRoles: [],
    unauthorizedMessage:
      'Sorry, you don’t have permission to view this content. If you think this is a mistake, please contact your admin.',
    redirectTo: '/project/edit/:projectUuid/:language', // Redirect to the parent route (edit-project)
  },
  // General access routes (Editor, Analytics, Producer, GroupAdmin)
  generalAccess: {
    allowedRoles: GENERAL_ACCESS_ROLES,
    unauthorizedMessage:
      'Sorry, you don’t have permission to view this content. If you think this is a mistake, please contact your admin.',
    redirectTo: getBaseAdminURL(),
  },
  // Read-only roles land here; ReadOnlyRouteGate keeps them off everything else.
  overview: {
    allowedRoles: [...GENERAL_ACCESS_ROLES, ...READ_ONLY_PROJECT_ROLES],
    unauthorizedMessage:
      'Sorry, you don’t have permission to view this content. If you think this is a mistake, please contact your admin.',
    redirectTo: getBaseAdminURL(),
  },
  editor: {
    allowedRoles: [EPermissions.Editor],
    unauthorizedMessage:
      'Sorry, you don’t have permission to view this content. If you think this is a mistake, please contact your admin.',
    redirectTo: getBaseAdminURL(),
  },
  developer: {
    allowedRoles: [EPermissions.Editor, EPermissions.Builder, EPermissions.GroupAdmin],
    unauthorizedMessage:
      'Sorry, you don’t have permission to view this content. If you think this is a mistake, please contact your admin.',
    redirectTo: '/project/edit/:projectUuid/:language',
  },
};

export const EDIT_PROJECT_ROUTES = new Map([
  [EDIT_PROJECT_ROUTE_IDS.CAST_AND_CREW, canAccessEditor],
  [EDIT_PROJECT_ROUTE_IDS.CALENDAR, ALL_ACCESS],
  [EDIT_PROJECT_ROUTE_IDS.TASKS, ALL_ACCESS],
  [EDIT_PROJECT_ROUTE_IDS.AUDIENCES, canAccessPeople],
  [EDIT_PROJECT_ROUTE_IDS.CAMPAIGNS, DEVELOPER_ONLY],
  [EDIT_PROJECT_ROUTE_IDS.CONTENT, ALL_ACCESS],
  [EDIT_PROJECT_ROUTE_IDS.LIVE_MONITOR, ALL_ACCESS],
  // 'edit-project-stats',
]);

export const EDIT_PROJECT_ROUTES_COMING_SOON = new Map([
  [EDIT_PROJECT_ROUTE_IDS.CREATIVE_BOARDS, ALL_ACCESS],
  [EDIT_PROJECT_ROUTE_IDS.STYLEPACKS, canAccessRtmp],
  [EDIT_PROJECT_ROUTE_IDS.RUN_OF_SHOW, ALL_ACCESS],
  [EDIT_PROJECT_ROUTE_IDS.SEGMENTS, canAccessRtmp],
  [EDIT_PROJECT_ROUTE_IDS.SCRIPTS, ALL_ACCESS],
  [EDIT_PROJECT_ROUTE_IDS.SLIDES, ALL_ACCESS],
]);

export const EDIT_PROJECT_ROUTES_CONFIG: EditProjectRouteConfig[] = [
  {
    id: EDIT_PROJECT_ROUTE_IDS.OVERVIEW,
    link_text: 'Overview',
    path: '',
    isIndex: true,
    icon: ICONS.HOME2,
    ...ROUTE_PERMISSIONS.overview,
    ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading your project overview." />,
    element: (
      <MainContentWrapper>
        <ProjectOverview />
      </MainContentWrapper>
    ),
  },
  {
    id: EDIT_PROJECT_ROUTE_IDS.SESSION,
    link_text: 'Session',
    path: 'session/:sessionUuid/*',
    icon: ICONS.INFO_CIRCLE,
    ...ROUTE_PERMISSIONS.generalAccess,
    ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading your project session." />,
    element: (
      <MainContentWrapper>
        <ProjectSession />
      </MainContentWrapper>
    ),
  },
  {
    id: EDIT_PROJECT_ROUTE_IDS.AGENDA,
    link_text: 'Agenda',
    path: 'agenda/*',
    icon: ICONS.CALENDAR2,
    ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading your project agenda." />,
    element: (
      <MainContentWrapper>
        <ComingSoon label="Agenda" />
      </MainContentWrapper>
    ),
    notAvailable: true,
  },
  {
    id: EDIT_PROJECT_ROUTE_IDS.CAST_AND_CREW,
    link_text: 'Cast & Crew',
    path: 'cast-and-crew/*',
    icon: ICONS.PEOPLE_NAV2,
    ErrorBoundary: () => (
      <EditProjectErrorBoundary label="Oops! There was an error loading your project cast and crew." />
    ),
    element: (
      <MainContentWrapper>
        <CastAndCrew />
      </MainContentWrapper>
    ),
  },
  {
    id: EDIT_PROJECT_ROUTE_IDS.CALENDAR,
    link_text: 'Calendar',
    path: 'calendar/*',
    icon: ICONS.CALENDAR2,
    ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading your project calendar." />,
    element: (
      <MainContentWrapper>
        <ComingSoon label="Calendar" />
      </MainContentWrapper>
    ),
    notAvailable: true,
  },
  {
    id: EDIT_PROJECT_ROUTE_IDS.TASKS,
    link_text: 'Tasks',
    path: 'tasks/*',
    icon: ICONS.CHECKLIST,
    ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading your project tasks." />,
    element: (
      <MainContentWrapper>
        <ComingSoon label="Tasks" />
      </MainContentWrapper>
    ),
    notAvailable: true,
  },
  {
    id: EDIT_PROJECT_ROUTE_IDS.CREATIVE_BOARDS,
    link_text: 'Creative Boards',
    path: 'creative-boards/*',
    icon: ICONS.CREATIVE_BOARD,
    ErrorBoundary: () => (
      <EditProjectErrorBoundary label="Oops! There was an error loading your project creative boards." />
    ),
    element: (
      <MainContentWrapper>
        <ComingSoon label="Creative Boards" />
      </MainContentWrapper>
    ),
    notAvailable: true,
  },
  {
    id: EDIT_PROJECT_ROUTE_IDS.STYLEPACKS,
    link_text: 'Stylepacks',
    path: 'stylepacks/*',
    icon: ICONS.STYLEPACKS,
    loader: stylepacksLoader,
    shouldRevalidate: () => false,
    ErrorBoundary: () => (
      <EditProjectErrorBoundary label="Oops! There was an error loading your project style packs." />
    ),
    element: <Outlet />,
    children: [
      {
        index: true,
        id: EDIT_PROJECT_ROUTE_IDS.STYLEPACKS_INDEX,
        element: (
          <MainContentWrapper>
            <Stylepacks />
          </MainContentWrapper>
        ),
      },
      {
        path: 'browse/*',
        id: EDIT_PROJECT_ROUTE_IDS.STYLEPACKS_BROWSE,
        element: <BrowseStylepacks />,
      },
      {
        path: 'preview/:stylepackId',
        id: EDIT_PROJECT_ROUTE_IDS.PREVIEW_STYLEPACK,
        element: <PreviewStylepack />,
      },
    ],
  },
  {
    // This will href replace to moderator
    id: EDIT_PROJECT_ROUTE_IDS.MODERATOR,
    link_text: 'Moderator',
    icon: ICONS.MODERATOR_2,
  },
  {
    id: EDIT_PROJECT_ROUTE_IDS.GREENROOM,
    link_text: 'Greenroom®',
    icon: ICONS.GREENROOM,
    ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading your project greenroom." />,
    element: (
      <MainContentWrapper>
        <ComingSoon label="Greenroom®" />
      </MainContentWrapper>
    ),
  },
  {
    id: EDIT_PROJECT_ROUTE_IDS.CONTENT,
    link_text: 'Content',
    path: 'content/*',
    icon: ICONS.VIDEO,
    ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading your project content." />,
    element: (
      <MainContentWrapper>
        <ComingSoon label="Content" />
      </MainContentWrapper>
    ),
    notAvailable: true,
  },
  {
    // External link opened via onClick
    id: EDIT_PROJECT_ROUTE_IDS.LIVE_MONITOR,
    link_text: 'Live Monitor',
    icon: ICONS.LIVE_SIGNAL,
  },
  {
    id: EDIT_PROJECT_ROUTE_IDS.SEGMENTS,
    link_text: 'Segments',
    path: 'segments/*',
    icon: ICONS.SEGMENTS,
    ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading your project segments." />,
    async lazy() {
      const Segments = await import('../grid-areas/main/segments/segments.tsx');
      return {
        Component: () => (
          <MainContentWrapper>
            <Segments.default />
          </MainContentWrapper>
        ),
      };
    },
  },
  {
    id: EDIT_PROJECT_ROUTE_IDS.RUN_OF_SHOW,
    link_text: 'Run of Show',
    path: 'run-of-show/*',
    icon: ICONS.RUN_OF_SHOW,
    ErrorBoundary: () => (
      <EditProjectErrorBoundary label="Oops! There was an error loading your project run of show." />
    ),
    element: (
      <MainContentWrapper>
        <ComingSoon label="Run of Show" />
      </MainContentWrapper>
    ),
    notAvailable: true,
  },
  {
    id: EDIT_PROJECT_ROUTE_IDS.SCRIPTS,
    link_text: 'Scripts',
    path: 'scripts/*',
    icon: ICONS.SCRIPTS,
    ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading your project scripts." />,
    element: (
      <MainContentWrapper>
        <ComingSoon label="Scripts" />
      </MainContentWrapper>
    ),
    notAvailable: true,
  },
  {
    id: EDIT_PROJECT_ROUTE_IDS.SLIDES,
    link_text: 'Slides',
    path: 'slides/*',
    icon: ICONS.MEDIA,
    ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading your project slides." />,
    element: (
      <MainContentWrapper>
        <ComingSoon label="Slides" />
      </MainContentWrapper>
    ),
    notAvailable: true,
  },
  {
    id: EDIT_PROJECT_ROUTE_IDS.AUDIENCES,
    path: 'audiences',
    link_text: 'Audiences',
    ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading your project audiences." />,
    icon: ICONS.USERS_AUDIENCE,
    element: <AudiencesRouteGate />,
    loader: audiencesLoader,
    children: [
      {
        index: true,
        element: <Navigate to={AUDIENCES_VIEW.LISTS} replace />,
      },
      {
        path: 'lists',
        id: EDIT_PROJECT_ROUTE_IDS.AUDIENCE_LISTS,
        ErrorBoundary: () => (
          <EditProjectErrorBoundary label="Oops! There was an error loading your project audiences." />
        ),
        element: <Audiences />,
      },
      {
        path: 'registrants',
        id: EDIT_PROJECT_ROUTE_IDS.REGISTRANTS_LIST,
        ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading registrant list." />,
        element: (
          <MainContentWrapper>
            <Registrants />
          </MainContentWrapper>
        ),
      },
      {
        path: 'list/:passcodeListId',
        id: EDIT_PROJECT_ROUTE_IDS.AUDIENCE_LIST,
        ErrorBoundary: () => (
          <EditProjectErrorBoundary label="Oops! There was an error loading your project audience list." />
        ),
        element: (
          <MainContentWrapper>
            <AudienceList />
          </MainContentWrapper>
        ),
      },
      {
        path: ':listType',
        id: EDIT_PROJECT_ROUTE_IDS.USERS_LIST,
        ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading registrant list." />,
        element: (
          <MainContentWrapper>
            <AllProfiles />
          </MainContentWrapper>
        ),
      },
    ],
  },
  {
    id: EDIT_PROJECT_ROUTE_IDS.CAMPAIGNS,
    link_text: 'Campaigns',
    path: 'campaigns/*',
    icon: ICONS.AI_SEND_MESSAGE,
    ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading your project campaigns." />,
    element: <Outlet />,
    ...ROUTE_PERMISSIONS.developer,
    children: [
      {
        index: true,
        id: EDIT_PROJECT_ROUTE_IDS.CAMPAIGNS_LIST,
        element: (
          <MainContentWrapper>
            <Campaigns />
          </MainContentWrapper>
        ),
      },
      {
        path: ':campaignUuid/edit',
        id: EDIT_PROJECT_ROUTE_IDS.CAMPAIGN_EDITOR,
        element: <CampaignEditor />,
      },
    ],
  },
  // {
  //   id: 'edit-project-stats',
  //   link_text: 'Stats',
  //   path: 'stats/*',
  //   icon: ICONS.STATS,
  //   ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading your project stats." />,
  //   shouldRevalidate: () => false,
  //   async lazy() {
  //     const CsvReportAlerts = await import('../grid-areas/main/analytics/csvs/csv-report-alerts.tsx');
  //     return {
  //       Component: () => (
  //         <RouteGuard
  //           allowedRoles={ROUTE_PERMISSIONS.adminOnly.allowedRoles}
  //           unauthorizedMessage={ROUTE_PERMISSIONS.adminOnly.unauthorizedMessage}
  //           redirectTo={ROUTE_PERMISSIONS.adminOnly.redirectTo}
  //         >
  //           <CsvReportAlerts.default />
  //           <StatsCleanupWrapper>
  //             <Outlet />
  //           </StatsCleanupWrapper>
  //         </RouteGuard>
  //       ),
  //     };
  //   },
  //   children: [
  //     {
  //       id: 'project-stats',
  //       index: true,
  //       async lazy() {
  //         const ProjectAnalytics = await import(
  //           '../grid-areas/main/analytics/project-analytics/project-analytics-container.tsx'
  //         );
  //         return {
  //           Component: () => (
  //             <MainContentWrapper>
  //               <ProjectAnalytics.default />
  //             </MainContentWrapper>
  //           ),
  //         };
  //       },
  //     },
  //     {
  //       id: 'session-stats',
  //       path: 'session/:sessionUuid',
  //       shouldRevalidate: () => false,
  //       async lazy() {
  //         const SessionAnalytics = await import('../grid-areas/main/analytics/session-analytics/session-analytics.tsx');
  //         return {
  //           Component: () => (
  //             <MainContentWrapper>
  //               <SessionAnalytics.default />
  //             </MainContentWrapper>
  //           ),
  //         };
  //       },
  //     },
  //   ],
  // },
  {
    id: EDIT_PROJECT_ROUTE_IDS.SETTINGS,
    link_text: 'Settings',
    path: 'settings/*',
    icon: ICONS.SETTINGS,
    ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading your project settings." />,
    element: (
      <MainContentWrapper>
        <Settings />
      </MainContentWrapper>
    ),
    children: SettingsRoutes,
  },
  {
    id: EDIT_PROJECT_ROUTE_IDS.PREVIEW_TEMPLATE,
    path: 'project-preview/*',
    ErrorBoundary: () => <EditProjectErrorBoundary label="Oops! There was an error loading your project preview." />,
    shouldRevalidate: () => false,
    link_text: '',
    async lazy() {
      const PreviewProject = await import('../../preview-project/preview-project.view.tsx');
      return { Component: PreviewProject.default };
    },
  },
];

export const EDIT_PROJECT_ROUTES_CONFIG_MAP: { [key: string]: EditProjectRouteConfig } =
  EDIT_PROJECT_ROUTES_CONFIG.reduce((acc, cur) => ({ ...acc, [cur.id]: cur }), {});

// Helper function to convert config to RouteObject
export const convertConfigToRouteObject = (config: EditProjectRouteConfig): RouteObject => {
  const componentKey = config?.Component ? 'Component' : 'element';

  // Wrap element with RouteGuard if allowedRoles is specified
  const wrappedElement = config.allowedRoles ? (
    <RouteGuard
      allowedRoles={config.allowedRoles}
      blockedRoles={config.blockedRoles}
      unauthorizedMessage={config.unauthorizedMessage}
      redirectTo={config.redirectTo}
    >
      {config.element}
    </RouteGuard>
  ) : (
    config[componentKey]
  );

  if (config.isIndex) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { children, ...rest } = config;
    const routeObject: RouteObject = {
      ...rest,
      index: true,
      [componentKey]: config?.lazy ? undefined : wrappedElement,
    };
    return routeObject;
  } else {
    const routeObject: RouteObject = {
      ...config,
      [componentKey]: config?.lazy ? undefined : wrappedElement,
    };

    return routeObject;
  }
};
